import type { ReactElement } from 'react';
import type { IconProps } from '../icons/generated';
import type { AccordionButtonProps as AccordionButtonNewProps } from './AccordionButton';
import type { ButtonProps as ButtonNewProps } from './Button';
import type { FloatingButtonProps as FloatingButtonNewProps } from './FloatingButton';
import type { IconButtonProps as IconButtonNewProps } from './IconButton';
import type { PopoverButtonProps as PopoverButtonNewProps } from './PopoverButton';
import type { TextButtonProps as TextButtonNewProps } from './TextButton';
import type { ButtonColor, ButtonVariant } from './types';
import type { ButtonKind, IconButtonKind, IconButtonShape, TextButtonKind } from './types.old';

export interface OldButtonProps extends Omit<ButtonNewProps, 'variant' | 'color' | 'leftIcon' | 'rightIcon'> {
  /** @deprecated variant와 color를 대신 사용해주세요. */
  kind?: ButtonKind;
  /** @deprecated leftIcon을 대신 사용해주세요. */
  startIcon?: ReactElement<IconProps>;
  /** @deprecated rightIcon을 대신 사용해주세요. */
  endIcon?: ReactElement<IconProps>;
}

const button_kind_map: Record<ButtonKind, { variant: ButtonVariant; color: ButtonColor }> = {
  primary: { variant: 'filled', color: 'primary' },
  secondary: { variant: 'filled', color: 'secondary' },
  black: { variant: 'filled', color: 'emphasis' },
  negative: { variant: 'filled', color: 'destructive' },
  outlined_primary: { variant: 'outlined', color: 'primary' },
  outlined_secondary: { variant: 'outlined', color: 'secondary' },
  outlined_black: { variant: 'outlined', color: 'emphasis' },
  outlined_negative: { variant: 'outlined', color: 'destructive' },
};

export function convertOldButtonProps(props: OldButtonProps): ButtonNewProps {
  const { kind, startIcon, endIcon, ...restProps } = props;
  const converted_kind = kind ? button_kind_map[kind] : undefined;

  return {
    ...restProps,
    ...(converted_kind ?? {}),
    ...('startIcon' in props ? { leftIcon: startIcon } : {}),
    ...('endIcon' in props ? { rightIcon: endIcon } : {}),
  };
}

export interface OldAccordionButtonProps extends Omit<AccordionButtonNewProps, 'open'> {
  /** @deprecated open을 대신 사용해주세요. */
  opened?: boolean;
}

export function convertOldAccordionButtonProps(props: OldAccordionButtonProps): AccordionButtonNewProps {
  const { opened, ...restProps } = props;

  return {
    ...restProps,
    open: opened,
  };
}

export interface OldFloatingButtonProps extends Omit<FloatingButtonNewProps, 'open' | 'leftIcon'> {
  /** @deprecated open을 대신 사용해주세요. */
  opened?: boolean;
  /** @deprecated leftIcon을 대신 사용해주세요. */
  startIcon?: ReactElement<IconProps>;
}

export function convertOldFloatingButtonProps(props: OldFloatingButtonProps): FloatingButtonNewProps {
  const { opened, startIcon, ...restProps } = props;

  return {
    ...restProps,
    open: opened,
    leftIcon: startIcon,
  };
}

export interface OldIconButtonProps extends Omit<IconButtonNewProps, 'variant' | 'color' | 'shape'> {
  /** @deprecated variant와 color를 대신 사용해주세요. */
  kind?: IconButtonKind;
  /** @deprecated rectangle 대신 rounded를 사용해주세요. */
  shape?: IconButtonShape;
}

const icon_button_kind_map: Record<
  IconButtonKind,
  { variant: NonNullable<IconButtonNewProps['variant']>; color: NonNullable<IconButtonNewProps['color']> }
> = {
  outlined_primary: { variant: 'outlined', color: 'primary' },
  outlined_secondary: { variant: 'outlined', color: 'secondary' },
  outlined_negative: { variant: 'outlined', color: 'destructive' },
};

export function convertOldIconButtonProps(props: OldIconButtonProps): IconButtonNewProps {
  const { kind, shape, ...restProps } = props;
  const converted_kind = kind ? icon_button_kind_map[kind] : undefined;

  return {
    ...restProps,
    variant: converted_kind?.variant,
    color: converted_kind?.color,
    shape: shape === 'rectangle' ? 'rounded' : shape,
  };
}

export interface OldTextButtonProps extends Omit<TextButtonNewProps, 'variant' | 'leftIcon' | 'rightIcon'> {
  /** @deprecated variant를 대신 사용해주세요. */
  kind?: TextButtonKind;
  /** @deprecated leftIcon을 대신 사용해주세요. */
  startIcon?: ReactElement<IconProps>;
  /** @deprecated rightIcon을 대신 사용해주세요. */
  endIcon?: ReactElement<IconProps>;
}

export function convertOldTextButtonProps(props: OldTextButtonProps): TextButtonNewProps {
  const { kind, startIcon, endIcon, ...restProps } = props;

  return {
    ...restProps,
    variant: kind,
    leftIcon: startIcon,
    rightIcon: endIcon,
  };
}

export interface OldPopoverButtonProps<OptionValue extends string | number>
  extends Omit<
    PopoverButtonNewProps<OptionValue>,
    'buttonType' | 'optionsMatchButtonWidth' | 'variant' | 'color' | 'leftIcon'
  > {
  /** @deprecated buttonType을 대신 사용해주세요. */
  useIconButton?: boolean;
  /** @deprecated optionsMatchButtonWidth를 대신 사용해주세요. */
  optionsMatchRefWidth?: boolean;
  /** @deprecated variant와 color를 대신 사용해주세요. */
  kind?: ButtonKind;
  /** @deprecated leftIcon을 대신 사용해주세요. */
  startIcon?: ReactElement<IconProps>;
}

export function convertOldPopoverButtonProps<OptionValue extends string | number>(
  props: OldPopoverButtonProps<OptionValue>,
): PopoverButtonNewProps<OptionValue> {
  const { useIconButton, optionsMatchRefWidth, kind, startIcon, ...restProps } = props;
  const converted_kind = kind ? button_kind_map[kind] : undefined;

  return {
    ...restProps,
    buttonType: useIconButton === undefined ? undefined : useIconButton ? 'icon-button' : 'button',
    optionsMatchButtonWidth: optionsMatchRefWidth,
    variant: converted_kind?.variant,
    color: converted_kind?.color,
    leftIcon: startIcon,
  };
}
